import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, TableContainer, Button, Chip, IconButton, CircularProgress,
  Tooltip, TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Menu, MenuItem, Checkbox, FormControlLabel, Divider, Stack,
  TablePagination, Select, FormControl, TableSortLabel
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CloseIcon from '@mui/icons-material/Close';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import config from '../config';
import { TechnicalChart } from '../components/TechnicalAnalysis/TechnicalChart';
import { AlertModal } from '../components/alerts/AlertModal';
import LoginModal from '../components/LoginModal';

/* ==========================================================================
    CODE-LEVEL COLUMN CONFIGURATION
   ========================================================================== */
const DEFAULT_COLUMN_CONFIG = [
  { id: 'close', label: 'Close ($)', align: 'right', enabled: true, defaultVisible: true, lockVisible: false },
  { id: 'signal', label: 'Signal', align: 'center', enabled: true, defaultVisible: true, lockVisible: false },
  { id: 'rsi', label: 'RSI (14)', align: 'right', enabled: true, defaultVisible: true, lockVisible: false },
  { id: 'macd', label: 'MACD', align: 'right', enabled: true, defaultVisible: true, lockVisible: false },
  { id: 'macd_signal', label: 'MACD Signal', align: 'right', enabled: true, defaultVisible: true, lockVisible: false },
  { id: 'sma_20', label: 'SMA 20', align: 'right', enabled: true, defaultVisible: true, lockVisible: false },
  { id: 'sma_50', label: 'SMA 50', align: 'right', enabled: true, defaultVisible: true, lockVisible: false },
  { id: 'sma_200', label: 'SMA 200', align: 'right', enabled: true, defaultVisible: true, lockVisible: false },
  { id: 'bb_upper', label: 'BB Upper', align: 'right', enabled: false, defaultVisible: true, lockVisible: false },
  { id: 'bb_lower', label: 'BB Lower', align: 'right', enabled: false, defaultVisible: true, lockVisible: false },
  { id: 'chart', label: 'Chart Modal', align: 'center', enabled: true, defaultVisible: true, lockVisible: false },
  { id: 'details', label: 'Details Tab', align: 'center', enabled: true, defaultVisible: true, lockVisible: false },
  { id: 'alert', label: 'Set Alert', align: 'center', enabled: true, defaultVisible: true, lockVisible: false },
];

const ACTIVE_COLUMNS = DEFAULT_COLUMN_CONFIG.filter(col => col.enabled);

const LOCAL_STORAGE_KEY = 'ta_dashboard_column_prefs_v2';
const CHART_INDICATORS_KEY = 'ta_dashboard_indicator_prefs';

export const TechnicalAnalysisDashboard = () => {
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Sorting State
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState('asc');

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [totalCount, setTotalCount] = useState(0);

  // Column Visibility State
  const [visibleColumns, setVisibleColumns] = useState(() => {
    let saved = {};
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) saved = JSON.parse(stored);
    } catch (e) {
      console.error('Failed to load column preferences:', e);
    }

    return ACTIVE_COLUMNS.reduce((acc, col) => {
      const isVisible = col.lockVisible ? true : (saved[col.id] !== undefined ? saved[col.id] : col.defaultVisible);
      return { ...acc, [col.id]: isVisible };
    }, {});
  });

  // Indicator Visibility State for Pop-up Chart
  const [indicatorsVisible, setIndicatorsVisible] = useState(() => {
    try {
      const saved = localStorage.getItem(CHART_INDICATORS_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error('Failed to load indicator preferences:', e);
    }
    return { sma20: true, sma50: true, sma200: true, bollinger: true };
  });

  // Column Menu anchor
  const [anchorElColumnMenu, setAnchorElColumnMenu] = useState(null);

  // Selected instrument for pop-up chart modal
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [activeSymbol, setActiveSymbol] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  // Alert Modal state
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertModalSymbol, setAlertModalSymbol] = useState(null);

  // Auth Guard / Login Modal state
  const [loginVisible, setLoginVisible] = useState(false);
  const [pendingAlertSymbol, setPendingAlertSymbol] = useState(null);

  // Save Column preferences to LocalStorage
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(visibleColumns));
    } catch (e) {
      console.error('Failed to save column preferences:', e);
    }
  }, [visibleColumns]);

  // Save Chart Indicator preferences to LocalStorage
  useEffect(() => {
    try {
      localStorage.setItem(CHART_INDICATORS_KEY, JSON.stringify(indicatorsVisible));
    } catch (e) {
      console.error('Failed to save indicator preferences:', e);
    }
  }, [indicatorsVisible]);

  const toggleColumnVisibility = (colId) => {
    const colConfig = ACTIVE_COLUMNS.find(c => c.id === colId);
    if (colConfig?.lockVisible) return;

    setVisibleColumns(prev => ({ ...prev, [colId]: !prev[colId] }));
  };

  const handleSelectAllColumns = (selectAll) => {
    const updated = ACTIVE_COLUMNS.reduce((acc, col) => {
      return { ...acc, [col.id]: col.lockVisible ? true : selectAll };
    }, {});
    setVisibleColumns(updated);
  };

  const toggleIndicator = (indicatorKey) => {
    setIndicatorsVisible(prev => ({
      ...prev,
      [indicatorKey]: !prev[indicatorKey]
    }));
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Token ${token}` } : {};
  };

  // Fetch current technical metrics with backend pagination and search support
  const fetchInstrumentsData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1, // API pages are 1-indexed; MUI is 0-indexed
        page_size: rowsPerPage,
      };

      if (searchTerm.trim() !== '') {
        params.search = searchTerm.trim();
      }

      const res = await axios.get(`${config.API_BASE_URL}api/technical-analysis/screener/`, {
        headers: getAuthHeaders(),
        params
      });

      if (res.data.results) {
        setInstruments(res.data.results);
        setTotalCount(res.data.count || 0);
      } else {
        setInstruments(res.data);
        setTotalCount(res.data.length || 0);
      }
    } catch (err) {
      console.error('Failed to fetch instrument metrics:', err);
      setInstruments([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchInstrumentsData();
  }, [fetchInstrumentsData]);

  // Reset to first page when search query changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle column header sort requests
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort instruments array if a sort column is active
  const sortedInstruments = React.useMemo(() => {
    if (!orderBy) return instruments;

    return [...instruments].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle null/undefined values safely during sort
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return order === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }

      return order === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [instruments, orderBy, order]);

  // Total pages count for page jump dropdown
  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;

  // Fetch historical chart series for modal
  const fetchChartHistory = useCallback(async (symbol) => {
    if (!symbol) return;
    try {
      setChartLoading(true);
      const res = await axios.get(`${config.API_BASE_URL}api/technical-analysis/${symbol}/history/`, {
        headers: getAuthHeaders()
      });
      setChartData(res.data.results || res.data);
    } catch (err) {
      console.error(`Failed to fetch history for ${symbol}:`, err);
      setChartData([]);
    } finally {
      setChartLoading(false);
    }
  }, []);

  // Open Pop-Up Chart Modal
  const handleOpenChartModal = (symbol) => {
    setActiveSymbol(symbol);
    setChartModalOpen(true);
    fetchChartHistory(symbol);
  };

  // Close Chart Modal
  const handleCloseChartModal = () => {
    setChartModalOpen(false);
    setActiveSymbol(null);
    setChartData([]);
  };

  // Open Alert Modal (Protected by Auth Check)
  const handleOpenAlert = (symbol = null) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPendingAlertSymbol(symbol);
      setLoginVisible(true);
    } else {
      setAlertModalSymbol(symbol);
      setAlertModalOpen(true);
    }
  };

  // Callback executed on successful login from LoginModal
  const handleLoginSuccess = () => {
    setLoginVisible(false);
    setAlertModalSymbol(pendingAlertSymbol);
    setAlertModalOpen(true);
    setPendingAlertSymbol(null);
  };

  // Safe formatting helper
  const formatVal = (val, decimals = 2) => {
    return val !== null && val !== undefined ? Number(val).toFixed(decimals) : '-';
  };

  // Dynamic colspan calculation based on active columns
  const activeColCount = 1 + ACTIVE_COLUMNS.filter(c => visibleColumns[c.id]).length;

  return (
    <Box sx={{ p: 3, maxWidth: 1800, margin: '0 auto' }}>
      {/* Header Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#0F172A">
            Technical Analysis & Alert Hub
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Live technical values across all instruments with instant pop-up charts and alarm configuration
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchInstrumentsData}
          >
            Refresh Data
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<NotificationsActiveIcon />}
            onClick={() => handleOpenAlert(null)}
          >
            Manage All Alerts
          </Button>
        </Box>
      </Box>

      {/* Full Data Table Paper */}
      <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
        <Box p={2} display="flex" justifyContent="space-between" alignItems="center" borderBottom="1px solid #E2E8F0">
          <TextField
            size="small"
            placeholder="Search ticker symbol..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ width: 300 }}
          />

          <Box display="flex" alignItems="center" gap={2}>
            {/* Column Picker Button */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={(e) => setAnchorElColumnMenu(e.currentTarget)}
            >
              Columns
            </Button>

            {/* Column Selector Dropdown Menu */}
            <Menu
              anchorEl={anchorElColumnMenu}
              open={Boolean(anchorElColumnMenu)}
              onClose={() => setAnchorElColumnMenu(null)}
              PaperProps={{ sx: { maxHeight: 400, width: 230, px: 1, py: 0.5 } }}
            >
              <Box display="flex" justifyContent="space-between" px={1} py={0.5}>
                <Button size="small" onClick={() => handleSelectAllColumns(true)}>Show All</Button>
                <Button size="small" color="error" onClick={() => handleSelectAllColumns(false)}>Hide All</Button>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              {ACTIVE_COLUMNS.map((col) => (
                <MenuItem key={col.id} disableGutters sx={{ py: 0, px: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!visibleColumns[col.id]}
                        disabled={col.lockVisible}
                        onChange={() => toggleColumnVisibility(col.id)}
                      />
                    }
                    label={
                      <Typography variant="body2" color={col.lockVisible ? 'textSecondary' : 'textPrimary'}>
                        {col.label} {col.lockVisible ? '(Locked)' : ''}
                      </Typography>
                    }
                    sx={{ width: '100%', m: 0 }}
                  />
                </MenuItem>
              ))}
            </Menu>

            <Typography variant="caption" color="textSecondary">
              Total {totalCount} instrument(s)
            </Typography>
          </Box>
        </Box>

        {/* Height expanded data window */}
        <TableContainer sx={{ height: 'calc(100vh - 200px)', minHeight: 700 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: '#F8FAFC' }}>
                  <TableSortLabel
                    active={orderBy === 'symbol'}
                    direction={orderBy === 'symbol' ? order : 'asc'}
                    onClick={() => handleRequestSort('symbol')}
                  >
                    <strong>Stock</strong>
                  </TableSortLabel>
                  <br />
                  <Typography variant="caption" color="textSecondary">
                    (Click symbol for details)
                  </Typography>
                </TableCell>

                {ACTIVE_COLUMNS.map((col) => (
                  visibleColumns[col.id] && (
                    <TableCell key={col.id} align={col.align} sx={{ backgroundColor: '#F8FAFC' }}>
                      {['chart', 'details', 'alert'].includes(col.id) ? (
                        <strong>{col.label}</strong>
                      ) : (
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : 'asc'}
                          onClick={() => handleRequestSort(col.id)}
                        >
                          <strong>{col.label}</strong>
                        </TableSortLabel>
                      )}
                    </TableCell>
                  )
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={activeColCount} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} />
                  </TableCell>
                </TableRow>
              ) : sortedInstruments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeColCount} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No technical data found for the current search.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedInstruments.map((item) => {
                  const signalColor = item.signal === 'BUY' ? 'success' : item.signal === 'SELL' ? 'error' : 'default';

                  return (
                    <TableRow key={item.symbol} hover>
                      {/* Symbol linked to details tab */}
                      <TableCell>
                        <Tooltip title={`Open ${item.symbol} analysis in a new tab`}>
                          <Typography
                            component={Link}
                            to={`/technical-analysis/${item.symbol}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            fontWeight={700}
                            color="primary"
                            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {item.symbol}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {visibleColumns.close && (
                        <TableCell align="right"><strong>{formatVal(item.close)}</strong></TableCell>
                      )}

                      {visibleColumns.signal && (
                        <TableCell align="center">
                          <Chip
                            label={item.signal || 'NEUTRAL'}
                            color={signalColor}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                      )}

                      {visibleColumns.rsi && (
                        <TableCell align="right">{formatVal(item.rsi, 1)}</TableCell>
                      )}

                      {visibleColumns.macd && (
                        <TableCell align="right">{formatVal(item.macd)}</TableCell>
                      )}

                      {visibleColumns.macd_signal && (
                        <TableCell align="right">{formatVal(item.macd_signal)}</TableCell>
                      )}

                      {visibleColumns.sma_20 && (
                        <TableCell align="right">{formatVal(item.sma_20)}</TableCell>
                      )}

                      {visibleColumns.sma_50 && (
                        <TableCell align="right">{formatVal(item.sma_50)}</TableCell>
                      )}

                      {visibleColumns.sma_200 && (
                        <TableCell align="right">{formatVal(item.sma_200)}</TableCell>
                      )}

                      {visibleColumns.bb_upper && (
                        <TableCell align="right">{formatVal(item.bb_upper)}</TableCell>
                      )}

                      {visibleColumns.bb_lower && (
                        <TableCell align="right">{formatVal(item.bb_lower)}</TableCell>
                      )}

                      {visibleColumns.chart && (
                        <TableCell align="center">
                          <Tooltip title={`Show Pop-up Chart for ${item.symbol}`}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenChartModal(item.symbol)}
                            >
                              <ShowChartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}

                      {visibleColumns.details && (
                        <TableCell align="center">
                          <Tooltip title={`Open ${item.symbol} full page in new tab`}>
                            <IconButton
                              size="small"
                              color="info"
                              component={Link}
                              to={`/technical-analysis/${item.symbol}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}

                      {visibleColumns.alert && (
                        <TableCell align="center">
                          <Tooltip title={`Set Alert Trigger for ${item.symbol}`}>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleOpenAlert(item.symbol)}
                            >
                              <NotificationsActiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Enhanced Single Line Pagination Bar */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
          sx={{
            borderTop: '1px solid #E2E8F0',
            '.MuiTablePagination-selectLabel': {
              margin: 0
            },
            '.MuiTablePagination-input': {
              marginRight: 1
            }
          }}
          labelDisplayedRows={({ from, to, count }) => (
            <Box component="span" display="inline-flex" alignItems="center" gap={1} ml={1}>
              <Typography variant="body2" color="textSecondary" component="span">
                Page:
              </Typography>
              <FormControl size="small" variant="outlined" sx={{ minWidth: 75 }}>
                <Select
                  value={page}
                  onChange={(e) => setPage(Number(e.target.value))}
                  sx={{
                    height: 28,
                    fontSize: '0.8125rem',
                    '.MuiSelect-select': { py: 0.5, px: 1 }
                  }}
                >
                  {Array.from({ length: totalPages }, (_, i) => (
                    <MenuItem key={i} value={i} sx={{ fontSize: '0.8125rem' }}>
                      {i + 1} / {totalPages}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="textSecondary" component="span" ml={1}>
                ({from}–{to} of {count !== -1 ? count : `more than ${to}`})
              </Typography>
            </Box>
          )}
        />
      </Paper>

      {/* Pop-up Chart Modal Dialog */}
      <Dialog
        open={chartModalOpen}
        onClose={handleCloseChartModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            {activeSymbol} Technical Analysis Chart
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              variant="outlined"
              color="info"
              startIcon={<OpenInNewIcon />}
              component={Link}
              to={`/technical-analysis/${activeSymbol}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Full Page
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<NotificationsActiveIcon />}
              onClick={() => handleOpenAlert(activeSymbol)}
            >
              Set {activeSymbol} Alert
            </Button>
            <IconButton onClick={handleCloseChartModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              backgroundColor: '#F8FAFC',
              borderRadius: 2,
              border: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="#334155">
              Chart Indicators:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={indicatorsVisible.sma20}
                    onChange={() => toggleIndicator('sma20')}
                    sx={{ color: '#2563EB', '&.Mui-checked': { color: '#2563EB' } }}
                  />
                }
                label={<Typography variant="body2" fontWeight={600} sx={{ color: '#2563EB' }}>SMA 20</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={indicatorsVisible.sma50}
                    onChange={() => toggleIndicator('sma50')}
                    sx={{ color: '#D97706', '&.Mui-checked': { color: '#D97706' } }}
                  />
                }
                label={<Typography variant="body2" fontWeight={600} sx={{ color: '#D97706' }}>SMA 50</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={indicatorsVisible.sma200}
                    onChange={() => toggleIndicator('sma200')}
                    sx={{ color: '#DB2777', '&.Mui-checked': { color: '#DB2777' } }}
                  />
                }
                label={<Typography variant="body2" fontWeight={600} sx={{ color: '#DB2777' }}>SMA 200</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={indicatorsVisible.bollinger}
                    onChange={() => toggleIndicator('bollinger')}
                    sx={{ color: '#64748B', '&.Mui-checked': { color: '#64748B' } }}
                  />
                }
                label={<Typography variant="body2" fontWeight={600} sx={{ color: '#64748B' }}>Bollinger Bands</Typography>}
              />
            </Stack>
          </Paper>

          {chartLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={520}>
              <CircularProgress />
            </Box>
          ) : chartData.length > 0 ? (
            <TechnicalChart data={chartData} indicatorsVisible={indicatorsVisible} />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height={400} bgcolor="#F8FAFC" borderRadius={2}>
              <Typography color="textSecondary">No chart history found for {activeSymbol}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChartModal}>Close Chart</Button>
        </DialogActions>
      </Dialog>

      {/* Alert Manager Modal */}
      <AlertModal
        open={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
        symbol={alertModalSymbol}
      />

      {/* Login Guard Modal */}
      <LoginModal
        visible={loginVisible}
        onClose={() => setLoginVisible(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </Box>
  );
};